import { FormEvent, useEffect, useState } from 'react';
import { useTexts } from 'utils/useTexts';
import { useFormContext } from '@context/FormContext';
import { Screens } from '@shared/constants/screens';
import { CDX_ACTIONS } from 'types/form';
import { postSelectedLoan, useGetLoanDetails } from '@services/lenderInformation';
import OverlaySpinner from '@components/shared/OverlaySpinner/OverlaySpinner';
import { ILoanDetails } from '@services/getAccurateEstimatedOffer';
import { Button } from '@components/shared/Button/Button';
import { Error } from '@components/shared/form/Error';
import { SEGMENT_SCREEN_PARAMS } from '@shared/constants/segmentConstants';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { camelToSnake } from 'utils/stringFormat';
import { useScreenNavigator } from '@shared/hooks/useScreenNavigator';
import { createErrorResponse, getContactData } from '../../services/utils';
import LoanCard from './LoanCard';


const LoanSelection = () => {
  const { t } = useTexts();
  const { next } = useScreenNavigator();
  const { trackFormSubmit, trackPage, trackClick, startInteraction } = useAnalytics(SEGMENT_SCREEN_PARAMS.LENDER_DETAILS);
  const { currentStep, handleStep, dispatch, submitStart, submitSuccess, isLoading } = useFormContext();
  const [selectedLoan, setSelectedLoan] = useState<ILoanDetails | null>();
  const [selectedID, setSelectedID] = useState<string | undefined>();
  const [loanSelectedValidation, setLoanSelectedValidation] = useState('');

  const contactData = getContactData();
  const { data: loans, isLoading: isCreditSumaryLoading } = useGetLoanDetails(
    {
      contactId: contactData?.contactId,
      leadId: contactData?.leadId,
    },
    contactData?.currentMonthlyPayment
  );



  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loans && selectedID) {
      setLoanSelectedValidation('');
      submitStart();
      const selectedLoanDetails = {
        leadId: contactData?.leadId,
        lender: selectedLoan?.lender ?? '',
        currentBalance: selectedLoan?.currentBalance ?? 0,
        payment: selectedLoan?.payment ?? 0,
        remainingTerm: selectedLoan?.remainingTerm ?? 0,
      };
      const submitResponse = await postSelectedLoan(
        {
          contactId: contactData?.contactId,
          assetId: contactData?.assetId,
        },
        selectedLoanDetails
      );
      if (!submitResponse) {
        trackFormSubmit('No asset id found for loan selection navigated to first screen');
        localStorage.clear();
        dispatch({
          type: CDX_ACTIONS.SUBMIT_API_ERROR,
          payload: createErrorResponse(400, 'Error while Loan selection, please try again'),
        });
        return;
      }
      if ('error' in submitResponse) {
        trackFormSubmit('Form Submitted', {
          submission_status: false,
          submission_method: 'select',
          submission_fail_reason : camelToSnake(submitResponse?.error?.message ??'')
        });
        trackFormSubmit('Lender Information Provided', {
          submission_method: 'select',
          form_context: 'lender_details',
          current_lender_name: selectedLoan?.lender ?? '',
          qualified_fail_reason: 'other',
          qualified_status: false,
          completed_status: false,
        });
        dispatch({
          type: CDX_ACTIONS.SUBMIT_API_ERROR,
          payload: submitResponse,
        });
      } else {
        submitSuccess();
        trackFormSubmit('Form Submitted', {
          submission_status: true,
          submission_method: 'select',
          loan_selected_display_name: selectedLoan?.lender ?? '',
          loan_selected_monthly_payment: selectedLoan?.payment ?? 0,
          loan_selected_estimated_balance:  selectedLoan?.currentBalance ?? 0,
          forward_screen_name: 'offers_confirmation',
        });
        trackFormSubmit('Lender Information Provided', {
          submission_method: 'select',
          form_context: 'lender_details',
          current_lender_name: selectedLoan?.lender ?? '',
          qualified_status: true,
          completed_status: true,
        });
      }
      next(currentStep, 'postSubmitOutcome', { submitResponse }, true);
    } else {
      setLoanSelectedValidation(t('loanSelection.validation'));
    }
  };

  useEffect(() => {
    if (!Array.isArray(loans)) return;

    next(currentStep, 'postSubmitOutcome', { loansCount: loans.length}, true);
    if (loans.length !== 0) {
      trackPage(t('pageTitles.lenderInformation'), { loan_match_count: loans.length });
    }
  }, [loans]);

  const handleLoanSelection = (id: string | undefined, loan: ILoanDetails) => {
    startInteraction();
    setSelectedID(id);
    setSelectedLoan(loan);
    setLoanSelectedValidation('');
  };

  const handleIDoNotSeeLoan = () => {

    trackClick('CTA Clicked', {
      cta_id: 'loan_not_listed',
      cta_text: 'I DON\'T SEE MY LOAN',
      cta_type: 'link',
      cta_context: 'lender_auto_match',
      loan_match_count: loans ? loans?.length : 0,
    });
    handleStep(Screens.LENDER_INFO);
  };
  return (
    <>
      <form onSubmit={handleSubmit}>
        <h1 className="iLendingCDX-form-title">{t('loanSelection.selectLoanAssociated')}</h1>
        <div className="iLendingCDX-loanselection-container">
          {loans &&
            loans.map((loan, index) => {
              const loanId = `${loan?.lender}-loan-${loan?.currentBalance}-${index}`;
              return (
                <LoanCard
                  key={loanId}
                  id={loanId}
                  loan={loan}
                  selectedID={selectedID}
                  handleLoanSelection={handleLoanSelection}
                />
              );
            })}
        </div>
        <div className="iLendingCDX-text-center">
          {loanSelectedValidation.length > 0 && <Error errorMessage={loanSelectedValidation} />}
        </div>
        <Button
          variant="ghost"
          size="lg"
          className={'iLendingCDX-mb-4'}
          onClick={handleIDoNotSeeLoan}
        >
          {t('loanSelection.iDontSeeLoan')}
        </Button>
        <Button
          className="iLendingCDX-button--rounded"
          size="lg"
          type="submit"
          isLoading={isLoading}
        >
          {t('loanSelection.continue')}
        </Button>
      </form>
      {isCreditSumaryLoading && <OverlaySpinner size="lg" />}
    </>
  );
};

export default LoanSelection;
